import { useRef, useEffect, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '@/hooks/useGameState';
import { GAME_CONSTANTS } from '@/types/game';

interface PlayerControllerProps {
  onPositionUpdate: (position: THREE.Vector3, quaternion: THREE.Quaternion) => void;
  onBoostStart?: () => void;
  onBoostStop?: () => void;
}

export const PlayerController = ({ onPositionUpdate, onBoostStart, onBoostStop }: PlayerControllerProps) => {
  const { camera, gl } = useThree();
  const playerSnake = useGameStore((state) => state.playerSnake);
  const setBoosting = useGameStore((state) => state.setBoosting);
  const removeSegment = useGameStore((state) => state.removeSegment);
  const virtualDirections = useGameStore((state) => state.virtualDirections);
  
  const positionRef = useRef(new THREE.Vector3(0, 0, 0));
  const quaternionRef = useRef(new THREE.Quaternion());
  const targetQuaternionRef = useRef(new THREE.Quaternion());
  const mousePositionRef = useRef(new THREE.Vector2(0, 0));
  const boostTimerRef = useRef(0);
  const prevBoostingRef = useRef(false);
  
  // Touch control state - use relative movement from initial touch point
  const touchStartRef = useRef(new THREE.Vector2(0, 0));
  const touchDeltaRef = useRef(new THREE.Vector2(0, 0));
  const isTouchActiveRef = useRef(false);
  
  // Keyboard state
  const keysRef = useRef({
    left: false,
    right: false,
    up: false,
    down: false,
    boost: false,
  });
  const useKeyboardRef = useRef(false);
  const useTouchRef = useRef(false);

  // Mouse move handler
  const handleMouseMove = useCallback((event: MouseEvent) => {
    const rect = gl.domElement.getBoundingClientRect();
    mousePositionRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mousePositionRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    useKeyboardRef.current = false; // Switch to mouse control
  }, [gl.domElement]);

  // Touch start handler - record initial touch position
  const handleTouchStart = useCallback((event: TouchEvent) => {
    event.preventDefault();
    if (event.touches.length === 1) {
      // Single touch - steering only
      const touch = event.touches[0];
      touchStartRef.current.set(touch.clientX, touch.clientY);
      touchDeltaRef.current.set(0, 0);
      isTouchActiveRef.current = true;
      useTouchRef.current = true;
      useKeyboardRef.current = false;
    } else if (event.touches.length >= 2) {
      // Two-finger touch - boost!
      setBoosting(true);
    }
  }, [setBoosting]);

  // Touch move handler - calculate relative movement from start position
  const handleTouchMove = useCallback((event: TouchEvent) => {
    event.preventDefault();
    if (event.touches.length === 1 && isTouchActiveRef.current) {
      const touch = event.touches[0];
      const rect = gl.domElement.getBoundingClientRect();
      // Calculate delta from start position, normalized by screen size
      // Much smaller sensitivity for mobile
      const sensitivity = 0.15; // Very low sensitivity for touch
      touchDeltaRef.current.x = ((touch.clientX - touchStartRef.current.x) / rect.width) * sensitivity;
      touchDeltaRef.current.y = -((touch.clientY - touchStartRef.current.y) / rect.height) * sensitivity;
      
      // Clamp to reasonable range
      touchDeltaRef.current.x = Math.max(-0.3, Math.min(0.3, touchDeltaRef.current.x));
      touchDeltaRef.current.y = Math.max(-0.3, Math.min(0.3, touchDeltaRef.current.y));
    }
  }, [gl.domElement]);

  // Touch end handler
  const handleTouchEnd = useCallback((event: TouchEvent) => {
    if (event.touches.length === 0) {
      // All fingers lifted
      isTouchActiveRef.current = false;
      touchDeltaRef.current.set(0, 0);
      setBoosting(false);
    } else if (event.touches.length === 1) {
      // Went from 2 fingers to 1 - stop boost, reset steering origin
      setBoosting(false);
      const touch = event.touches[0];
      touchStartRef.current.set(touch.clientX, touch.clientY);
      touchDeltaRef.current.set(0, 0);
    }
  }, [setBoosting]);

  // Keyboard handlers
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    switch (event.code) {
      case 'ArrowLeft':
      case 'KeyA':
        keysRef.current.left = true;
        useKeyboardRef.current = true;
        event.preventDefault();
        break;
      case 'ArrowRight':
      case 'KeyD':
        keysRef.current.right = true;
        useKeyboardRef.current = true;
        event.preventDefault();
        break;
      case 'ArrowUp':
      case 'KeyW':
        keysRef.current.up = true;
        useKeyboardRef.current = true;
        event.preventDefault();
        break;
      case 'ArrowDown':
      case 'KeyS':
        keysRef.current.down = true;
        useKeyboardRef.current = true;
        event.preventDefault();
        break;
      case 'Space':
        if (!keysRef.current.boost) {
          keysRef.current.boost = true;
          setBoosting(true);
        }
        event.preventDefault();
        break;
    }
  }, [setBoosting]);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    switch (event.code) {
      case 'ArrowLeft':
      case 'KeyA':
        keysRef.current.left = false;
        break;
      case 'ArrowRight':
      case 'KeyD':
        keysRef.current.right = false;
        break;
      case 'ArrowUp':
      case 'KeyW':
        keysRef.current.up = false;
        break;
      case 'ArrowDown':
      case 'KeyS':
        keysRef.current.down = false;
        break;
      case 'Space':
        keysRef.current.boost = false;
        setBoosting(false);
        break;
    }
  }, [setBoosting]);

  // Mouse boost handlers
  const handleMouseDown = useCallback((event: MouseEvent) => {
    if (event.button === 0) {
      setBoosting(true);
    }
  }, [setBoosting]);

  const handleMouseUp = useCallback((event: MouseEvent) => {
    if (event.button === 0) {
      setBoosting(false);
    }
  }, [setBoosting]);


  // Setup event listeners
  useEffect(() => {
    const element = gl.domElement;
    
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('mouseup', handleMouseUp);
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    // Keyboard listeners on window
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('mouseup', handleMouseUp);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gl.domElement, handleMouseMove, handleMouseDown, handleMouseUp, handleTouchMove, handleTouchStart, handleTouchEnd, handleKeyDown, handleKeyUp]);

  // Initialize position
  useEffect(() => {
    if (playerSnake) {
      positionRef.current.copy(playerSnake.position);
      quaternionRef.current.copy(playerSnake.quaternion);
    }
  }, [playerSnake?.id]);

  // Track boost state changes and call callbacks
  useEffect(() => {
    const isBoosting = playerSnake?.isBoosting ?? false;
    if (isBoosting !== prevBoostingRef.current) {
      prevBoostingRef.current = isBoosting;
      if (isBoosting) {
        onBoostStart?.();
      } else {
        onBoostStop?.();
      }
    }
  }, [playerSnake?.isBoosting, onBoostStart, onBoostStop]);

  useFrame((state, delta) => {
    if (!playerSnake || !playerSnake.isAlive) return;

    const keys = keysRef.current;
    const useKeyboard = useKeyboardRef.current;
    const useTouch = useTouchRef.current;
    
    // Get current orientation vectors
    const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternionRef.current);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternionRef.current);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternionRef.current);

    let targetDir: THREE.Vector3;

    // Check if virtual D-pad is being used
    const useVirtualDPad = virtualDirections.up || virtualDirections.down || virtualDirections.left || virtualDirections.right;

    if (useKeyboard || useVirtualDPad) {
      // Keyboard or virtual D-pad controls - camera-relative for consistent feel
      const turnSpeed = 2.5; // radians per second
      
      // Merge keyboard and virtual directions
      const activeUp = keys.up || virtualDirections.up;
      const activeDown = keys.down || virtualDirections.down;
      const activeLeft = keys.left || virtualDirections.left;
      const activeRight = keys.right || virtualDirections.right;
      
      // Calculate turn amounts
      let yawDelta = 0;
      let pitchDelta = 0;
      
      if (activeLeft) yawDelta += turnSpeed * delta;
      if (activeRight) yawDelta -= turnSpeed * delta;
      if (activeUp) pitchDelta += turnSpeed * delta;
      if (activeDown) pitchDelta -= turnSpeed * delta;
      
      // Apply yaw (left/right) rotation around world up - this is always consistent
      const yawQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawDelta);
      quaternionRef.current.premultiply(yawQuat);
      
      // For pitch, use camera-relative right axis so up/down always match screen direction
      // Get the camera's right vector projected onto horizontal plane
      const cameraRight = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
      cameraRight.y = 0; // Keep it horizontal
      cameraRight.normalize();
      
      // Apply pitch rotation around camera-relative right axis
      const pitchQuat = new THREE.Quaternion().setFromAxisAngle(cameraRight, pitchDelta);
      quaternionRef.current.premultiply(pitchQuat);
      
      quaternionRef.current.normalize();
    } else if (useTouch && isTouchActiveRef.current && !useVirtualDPad) {
      // Touch controls - use relative delta from touch start
      // Only use touch steering if D-pad is NOT being used
      const touchTurnSpeed = 3.0; // radians per second for touch
      
      // Apply gradual turning based on finger position relative to start
      const yawDelta = -touchDeltaRef.current.x * touchTurnSpeed * delta * 60;
      const pitchDelta = touchDeltaRef.current.y * touchTurnSpeed * delta * 60;
      
      // Apply yaw rotation around world up
      const yawQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawDelta);
      quaternionRef.current.premultiply(yawQuat);
      
      // Apply pitch using camera-relative right axis
      const cameraRight = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
      cameraRight.y = 0;
      cameraRight.normalize();
      const pitchQuat = new THREE.Quaternion().setFromAxisAngle(cameraRight, pitchDelta);
      quaternionRef.current.premultiply(pitchQuat);
      
      quaternionRef.current.normalize();
    } else {
      // Check if we're on a touch device - if so, only steer when actively touching
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      if (isTouchDevice) {
        // On mobile: only steer if actively dragging on the screen
        // Otherwise, snake continues in current direction (no steering)
        // This prevents snap-back when releasing D-pad
      } else {
        // Mouse controls (desktop only) - reduced sensitivity
        const planeDistance = 120;
        const planeCenter = positionRef.current.clone().add(forward.clone().multiplyScalar(planeDistance));
        
        const turnScale = 8;
        const targetPoint = planeCenter
          .add(right.clone().multiplyScalar(mousePositionRef.current.x * turnScale))
          .add(up.clone().multiplyScalar(mousePositionRef.current.y * turnScale));
        
        targetDir = targetPoint.sub(positionRef.current).normalize();
        
        const lookMatrix = new THREE.Matrix4().lookAt(
          new THREE.Vector3(0, 0, 0),
          targetDir,
          new THREE.Vector3(0, 1, 0)
        );
        targetQuaternionRef.current.setFromRotationMatrix(lookMatrix);

        quaternionRef.current.slerp(targetQuaternionRef.current, 0.03);
      }
    }

    // Move forward
    const moveDir = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternionRef.current);
    positionRef.current.add(moveDir.multiplyScalar(playerSnake.velocity * delta));

    // Boundary collision
    const halfWorld = GAME_CONSTANTS.WORLD_SIZE / 2 * 0.95;

    if (Math.abs(positionRef.current.x) > halfWorld) {
      positionRef.current.x = Math.sign(positionRef.current.x) * halfWorld;
    }
    if (Math.abs(positionRef.current.y) > halfWorld) {
      positionRef.current.y = Math.sign(positionRef.current.y) * halfWorld;
    }
    if (Math.abs(positionRef.current.z) > halfWorld) {
      positionRef.current.z = Math.sign(positionRef.current.z) * halfWorld;
    }

    // Handle boost drain
    if (playerSnake.isBoosting) {
      boostTimerRef.current += delta;
      if (boostTimerRef.current >= 1 / GAME_CONSTANTS.BOOST_DRAIN_RATE) {
        boostTimerRef.current = 0;
        removeSegment();
      }
    }

    // Update camera position (third-person following)
    const cameraOffset = new THREE.Vector3(0, 15, -45);
    cameraOffset.applyQuaternion(quaternionRef.current);
    const targetCameraPos = positionRef.current.clone().add(cameraOffset);
    
    camera.position.lerp(targetCameraPos, GAME_CONSTANTS.CAMERA_LERP);
    camera.lookAt(positionRef.current);

    // Notify parent of position update
    onPositionUpdate(positionRef.current.clone(), quaternionRef.current.clone());
  });

  return null;
};
